import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { COVER_DOC_LIMITS, type CoverTextBox } from 'fym-shared/src/music24/coverDoc';
import { KIT_FONT_FACES } from 'fym-shared/src/music24/releaseThemes';
import { TypedMemo } from '../../common/types';
import { coverEditorMessages } from '../coverEditorMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface InlineTextEditProps {
  box: CoverTextBox;
  side: number;
  onDone: (id: string, text: string) => void;
}

const CASE_CSS = { none: 'none', upper: 'uppercase', lower: 'lowercase' } as const;

/**
 * Typing in a text box on the cover (double-click): a text area in the place, the font, the size
 * and the colour of the box. The canvas leaves the box out while it is open. A click outside or
 * Escape ends it; Enter adds a line.
 */
const InlineTextEditComponent: React.FC<InlineTextEditProps> = ({ box, side, onDone }) => {
  const intl = useIntl();
  const [text, setText] = useState(box.text);
  const area = useRef<HTMLTextAreaElement>(null);
  const face = KIT_FONT_FACES[box.fontId];
  const fontPx = box.sizeShare * side;

  useEffect(() => {
    area.current?.focus();
    area.current?.select();
  }, []);

  const change = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(event.target.value.slice(0, COVER_DOC_LIMITS.textLength));
  }, []);
  const done = useCallback(() => onDone(box.id, text), [box.id, onDone, text]);
  const keyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // The keys of the editor (Delete, the arrows, Undo) must not act while the user types.
      event.stopPropagation();
      if (event.key === 'Escape') {
        event.preventDefault();
        done();
      }
    },
    [done]
  );
  const lines = Math.max(1, text.split('\n').length);

  return (
    <textarea
      ref={area}
      className={styles.inlineEdit}
      style={{
        left: box.x * side,
        top: box.y * side,
        width: box.width * side,
        height: Math.max(lines, 1) * fontPx * box.lineHeight + fontPx * 0.4,
        transform: `translate(-50%, -50%) rotate(${box.rotationDeg}deg)`,
        fontFamily: `'${face.family}', ${face.fallback}`,
        fontSize: fontPx,
        fontWeight: box.weight,
        fontStyle: box.italic || face.italic ? 'italic' : 'normal',
        lineHeight: box.lineHeight,
        letterSpacing: `${box.tracking}em`,
        textAlign: box.align,
        textTransform: CASE_CSS[box.textCase],
        color: box.colour,
      }}
      value={text}
      aria-label={intl.formatMessage(coverEditorMessages.textEditLabel)}
      onChange={change}
      onBlur={done}
      onKeyDown={keyDown}
      onPointerDown={(event) => event.stopPropagation()}
    />
  );
};

export const InlineTextEdit = TypedMemo(InlineTextEditComponent);
